import { IDestroyOptions, Sprite, TextStyle } from "pixi.js";
import { nameof } from "../../../../shared/bindable-data";
import { TextWithTime } from "../../common/components/TextWithTime";
import { GameFont } from "../../common/GameFont";
import { ButtonWithScaleAndPosYAnimation } from "../../common/components/ButtonWithScaleAndPosYAnimation";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { Sound } from "@pixi/sound";
import { IFortuneWheelModel } from "../../popups/hourlyBonus/model/IFortuneWheelModel";
import { GameResourcePackage } from "../resources/GameResourcePackage";

export class RollButton extends ButtonWithScaleAndPosYAnimation {
    private static readonly FREE_TEXT = "FREE ";
    private static readonly DICE_ROLLS_TEXT = " DICE\n ROLLS IN\n";
    private static readonly BONUS_WHEEL_TEXT = "BONUS\nWHEEL IN\n";
    private static readonly SWITCH_TEXT_REWARD_INDEX = 3;

    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly gameResources: GameResourcePackage;
    private readonly playerModel: IPlayerModel;
    private rollButtonIcon: Sprite;
    private freeSpinTimeText: TextWithTime;

    public constructor(
        gameResources: GameResourcePackage,
        fortuneWheelModel: IFortuneWheelModel,
        playerModel: IPlayerModel,
        pressSound?: Sound
    ) {
        super(gameResources.assets_views_game_ui_main_button_dice_button_bg_png, pressSound);
        this.gameResources = gameResources;
        this.fortuneWheelModel = fortuneWheelModel;
        this.playerModel = playerModel;
        this.build();
        this.fortuneWheelModel.propertyChanged.add(this.onFortuneWheelModelPropertyChanged, this);
        this.once("added", this.show);
    }

    private async onFortuneWheelModelPropertyChanged(model: IFortuneWheelModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "spinAvailableIn"):
                this.freeSpinTimeText.updateTime(model.spinAvailableIn.value || 0);
                this.rollButtonIcon.visible = model.spinAvailableIn.value == 0 || this.playerModel.rolls.value != 0;
                this.freeSpinTimeText.visible = model.spinAvailableIn.value != 0 && this.playerModel.rolls.value == 0;
                break;
            case nameof(model, "currentHourlyBonusSection"):
                this.freeSpinTimeText.updateDefaultText(this.getHourlyBonusTimeText());
                this.rollButtonIcon.visible = model.spinAvailableIn.value == 0 || this.playerModel.rolls.value != 0;
                this.freeSpinTimeText.visible = model.spinAvailableIn.value != 0 && this.playerModel.rolls.value == 0;
                break;
        }
    }

    private show(): void {
        this.onFortuneWheelModelPropertyChanged(this.fortuneWheelModel, "spinAvailableIn");
    }

    private getHourlyBonusTimeText(): string {
        const currentHourlyBonusSection = this.fortuneWheelModel.currentHourlyBonusSection.value;
        const currentHourlyBonusItemData = this.fortuneWheelModel.hourlyBonusItems.value[this.fortuneWheelModel.currentHourlyBonusSection.value];
        const bonusItemsCount = currentHourlyBonusItemData != null ? currentHourlyBonusItemData.freeDiceRollsReward : 0;
        const resultText = currentHourlyBonusSection == RollButton.SWITCH_TEXT_REWARD_INDEX ?
            RollButton.FREE_TEXT + RollButton.BONUS_WHEEL_TEXT :
            RollButton.FREE_TEXT + bonusItemsCount + RollButton.DICE_ROLLS_TEXT;
        return resultText;
    }

    private build(): void {
        this.rollButtonIcon = new Sprite(this.gameResources.assets_views_game_ui_main_button_dice_button_icon_png);
        this.rollButtonIcon.scale.set(0.9);
        this.rollButtonIcon.position.set((this.defaultView.width - this.rollButtonIcon.width) * 0.5, (this.defaultView.height - this.rollButtonIcon.height) * 0.5 + 8);
        this.defaultView.addChild(this.rollButtonIcon);

        this.freeSpinTimeText = new TextWithTime(this.fortuneWheelModel.spinAvailableIn.value || 0, this.getHourlyBonusTimeText(), "", new FreeSpinInTextStyle());
        this.freeSpinTimeText.position.set((this.defaultView.width - this.freeSpinTimeText.width) * 0.5, (this.defaultView.height - this.freeSpinTimeText.height) * 0.5 + 8);
        this.freeSpinTimeText.visible = false;
        this.addChild(this.freeSpinTimeText);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.fortuneWheelModel.propertyChanged.remove(this.onFortuneWheelModelPropertyChanged, this);
        super.destroy(options);
    }
}

class FreeSpinInTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 54,
            lineHeight: 55
        }));
    }
}
